import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Eyebrow, Reveal, SectionHeader, MaskedLines, EASE } from '../components/Reveal';
import Marquee from '../components/Marquee';
import Journey from '../components/Journey';
import ArticleCard from '../components/ArticleCard';
import { WhatsAppButton, BookButton } from '../components/CTAs';
import { TREATMENTS } from '../data/treatments';
import { TESTIMONIALS } from '../data/testimonials';
import { fetchArticles } from '../lib/api';
import { track } from '../lib/track';
import { DOCTORS } from '../data/doctors';

function Hero() {
    const { t, lang } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 64]);
    const h = t.home.hero;
    return (
        <section ref={ref} className="relative overflow-hidden pt-24 md:pt-32 lg:pt-36" data-testid="home-hero">
            <div className="pointer-events-none absolute -right-40 top-10 h-[540px] w-[540px] rounded-full bg-peri-200/70" aria-hidden="true" />
            <div className="pointer-events-none absolute -left-52 bottom-0 h-96 w-96 rounded-full bg-peri-100" aria-hidden="true" />
            <Container className="relative">
                <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE }}
                            className="inline-flex items-center gap-2 rounded-full border border-peri-300 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-700"
                        >
                            {h.badge}
                        </motion.p>
                        <MaskedLines
                            lines={[h.titleA, h.titleB]}
                            className="mt-6 font-serif text-[clamp(3.2rem,9vw,6.8rem)] font-semibold leading-[0.98] tracking-tight text-ink"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
                        >
                            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-muted md:text-xl">{h.sub}</p>
                            <p className="mt-4 max-w-xl text-base leading-relaxed text-ink-subtle">{h.sub2}</p>
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <a
                                    href="#pathways"
                                    data-testid="hero-cta-explore"
                                    onClick={() => track('explore_options_click', { source: 'hero' })}
                                    className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-500 hover:shadow-card-hover"
                                >
                                    {t.cta.explore}
                                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </a>
                                <WhatsAppButton label={t.cta.whatsapp} source="hero" className="border-2 border-gold-600 !bg-transparent !text-gold-700 hover:!bg-gold-50" />
                            </div>
                            <p className="mt-8 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-ink-subtle">
                                {h.welcome}
                            </p>
                        </motion.div>
                    </div>
                    <motion.div style={{ y }} className="relative mx-auto hidden w-full max-w-md lg:block">
                        <div className="absolute -left-6 top-10 h-full w-full rounded-b-[2rem] rounded-t-[999px] bg-peri-300" aria-hidden="true" />
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                            className="relative overflow-hidden rounded-b-[2rem] rounded-t-[999px] ring-1 ring-peri-300"
                        >
                            <img src="/img/smile-editorial.jpg" alt={h.imgAlt} className="aspect-[4/5] w-full object-cover" />
                            <div className="absolute inset-0 ring-1 ring-inset ring-gold-600/10" />
                            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white/95 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink shadow-subtle">
                                {h.imgCaption}
                            </span>
                        </motion.div>
                        <span className="absolute -right-4 top-16 rounded-full bg-ink px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-peri-200">
                            BM · EN · 中文 · தமிழ்
                        </span>
                    </motion.div>
                </div>
            </Container>
        </section>
    );
}

function Pathways() {
    const { t, lang } = useLang();
    return (
        <section id="pathways" className="scroll-mt-24 py-16 md:py-24" data-testid="home-pathways">
            <Container>
                <SectionHeader eyebrow={t.home.pathways.eyebrow} title={t.home.pathways.title} sub={t.home.pathways.sub} />
                <div className="grid gap-3 sm:grid-cols-2">
                    {t.home.pathways.items.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.05}>
                            <Link
                                to={`/dental/${lang}${p.to}`}
                                className="group flex items-center justify-between gap-4 rounded-2xl border border-peri-200 bg-white px-6 py-5 transition-all duration-300 hover:border-gold-500 hover:shadow-card-hover"
                                data-testid={`pathway-${p.id}`}
                            >
                                <span className="flex items-center gap-4">
                                    <span className="font-serif text-xl italic text-peri-400">{String(i + 1).padStart(2, '0')}</span>
                                    <span className="text-base font-medium text-ink">{p.label}</span>
                                </span>
                                <span className="text-gold-700 transition-transform duration-300 group-hover:translate-x-1">
                                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </Container>
        </section>
    );
}

function Pillars() {
    const { t, lang } = useLang();
    return (
        <section className="bg-peri-50 py-16 md:py-24" data-testid="home-pillars">
            <Container>
                <SectionHeader eyebrow={t.home.pillars.eyebrow} title={t.home.pillars.title} sub={t.home.pillars.sub} />
                <div className="grid gap-4 md:grid-cols-2">
                    {TREATMENTS.map((tr, i) => {
                        const c = tr[lang] || tr.en;
                        return (
                            <Reveal key={tr.slug} delay={i * 0.07} className="h-full">
                                <Link
                                    to={`/dental/${lang}/treatments/${tr.slug}`}
                                    data-testid={`pillar-${tr.slug}`}
                                    className="group flex h-full flex-col justify-between rounded-3xl border border-peri-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold-500 hover:shadow-card-hover md:p-9"
                                >
                                    <div>
                                        <span className="font-serif text-3xl italic text-peri-300">{String(i + 1).padStart(2, '0')}</span>
                                        <h3 className="mt-4 font-serif text-2xl font-semibold text-ink md:text-3xl">{c.name}</h3>
                                        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-ink-muted">{c.short}</p>
                                    </div>
                                    <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-700">
                                        {t.cta.explore}
                                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                                            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </Container>
        </section>
    );
}

function Why() {
    const { t } = useLang();
    return (
        <section className="py-16 md:py-24" data-testid="home-why">
            <Container>
                <SectionHeader eyebrow={t.home.why.eyebrow} title={t.home.why.title} />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {t.home.why.items.map((w, i) => (
                        <Reveal key={w.title} delay={i * 0.06} className="h-full">
                            <div className="h-full rounded-2xl bg-peri-50 p-6">
                                <span className="h-1 w-8 rounded-full bg-gold-600" />
                                <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{w.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{w.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Container>
        </section>
    );
}

function Veneers() {
    const { t, lang } = useLang();
    const v = t.home.veneers;
    return (
        <section className="py-16 md:py-24" data-testid="home-veneers">
            <Container>
                <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                    <Reveal>
                        <img src="/img/tr-veneers.jpg" alt={v.title} className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="veneers-img" loading="lazy" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Eyebrow>{v.eyebrow}</Eyebrow>
                        <h2 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-ink md:text-4xl">{v.title}</h2>
                        <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-muted md:text-lg">{v.text}</p>
                        <div className="mt-6 rounded-2xl border-l-4 border-gold-500 bg-peri-50 p-6" data-testid="veneers-artist">
                            <p className="font-serif text-lg font-semibold italic text-gold-700">{v.artist.title}</p>
                            <p className="mt-2 text-sm leading-relaxed text-ink-muted">{v.artist.text}</p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to={`/dental/${lang}/treatments/aesthetic-veneers`}
                                data-testid="veneers-cta"
                                onClick={() => track('veneers_cta', { source: 'home' })}
                                className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-ink transition-all hover:bg-gold-500"
                            >
                                {v.cta}
                            </Link>
                            <WhatsAppButton label={v.ctaWa} source="veneers" />
                        </div>
                    </Reveal>
                </div>
            </Container>
        </section>
    );
}

function Doctors() {
    const { t, lang } = useLang();
    return (
        <section className="bg-peri-50 py-16 md:py-24" data-testid="home-doctors">
            <Container>
                <SectionHeader eyebrow={t.home.doctors.eyebrow} title={t.home.doctors.title} sub={t.home.doctors.sub} />
                <div className="grid gap-5 md:grid-cols-2">
                    {DOCTORS.map((d, i) => (
                        <Reveal key={d.slug} delay={i * 0.08}>
                            <Link
                                to={`/dental/${lang}/doctors/${d.slug}`}
                                data-testid={`doctor-card-${d.slug}`}
                                className="group flex h-full gap-6 rounded-3xl border border-peri-200 bg-white p-6 transition-all duration-300 hover:border-gold-500 hover:shadow-card-hover md:p-8"
                            >
                                <img src={d.photo} alt={d.name} loading="lazy" className="aspect-[3/4] w-28 shrink-0 rounded-2xl border border-peri-200 object-cover md:w-36" data-testid={`doctor-img-${d.slug}`} />
                                <div className="flex flex-col">
                                    <h3 className="font-serif text-2xl font-semibold text-ink">{d.short}</h3>
                                    <p className="mt-1 text-sm font-semibold text-gold-700">{d.creds}</p>
                                    <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                                        {d.key === 'irma' ? t.home.doctors.irmaLine : t.home.doctors.ariandiLine}
                                    </p>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </Container>
        </section>
    );
}

function Digital() {
    const { t, lang } = useLang();
    const d = t.home.digital;
    return (
        <section className="py-16 md:py-24" data-testid="home-digital">
            <Container>
                <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                    <Reveal className="order-2 lg:order-1">
                        <img src="/img/tr-digital.jpg" alt={d.title} className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="digital-img" loading="lazy" />
                    </Reveal>
                    <Reveal delay={0.1} className="order-1 lg:order-2">
                        <Eyebrow>{d.eyebrow}</Eyebrow>
                        <h2 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-ink md:text-4xl">{d.title}</h2>
                        <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-muted md:text-lg">{d.p1}</p>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink-muted">{d.p2}</p>
                        <p className="mt-4 max-w-lg text-xs italic leading-relaxed text-ink-subtle">{d.note}</p>
                        <Link
                            to={`/dental/${lang}/digital-dentistry`}
                            data-testid="digital-cta"
                            onClick={() => track('digital_cta', { source: 'home' })}
                            className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-gold-500 px-6 py-3 text-sm font-semibold text-ink transition-all hover:bg-gold-50"
                        >
                            {d.cta}
                            <span>
                                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </Container>
        </section>
    );
}

function Teen() {
    const { t, lang } = useLang();
    return (
        <section className="py-16 md:py-24" data-testid="home-teen">
            <Container>
                <div className="grid items-center gap-10 lg:grid-cols-2">
                    <Reveal>
                        <Eyebrow>{t.home.teen.eyebrow}</Eyebrow>
                        <h2 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-ink md:text-4xl">{t.home.teen.title}</h2>
                        <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-muted md:text-lg">{t.home.teen.text}</p>
                        <ul className="mt-6 space-y-3">
                            {t.home.teen.points.map((p) => (
                                <li key={p} className="flex items-start gap-3 text-sm text-ink-body">
                                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gold-600" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={`/dental/${lang}/teen-smile`}
                            data-testid="teen-cta"
                            onClick={() => track('teen_cta', { source: 'home' })}
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-ink transition-all hover:bg-gold-500"
                        >
                            {t.home.teen.cta}
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="rounded-3xl border border-peri-200 bg-peri-50 p-8 md:p-10">
                            <p className="font-serif text-2xl italic leading-snug text-gold-700">
                                {t.teen.ctaTitle}
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-ink-muted">{t.teen.ctaText}</p>
                            <img
                                src="/img/clinic-team-entrance.jpg"
                                alt="Dr. Irma and the Klinik Pergigian Irma Ariandi team welcoming you at the clinic entrance"
                                loading="lazy"
                                className="mt-8 aspect-[4/5] w-full rounded-2xl border border-peri-200 object-cover"
                                data-testid="home-teen-clinic-photo"
                            />
                        </div>
                    </Reveal>
                </div>
            </Container>
        </section>
    );
}

function International() {
    const { t, lang } = useLang();
    const cards = [
        { ...t.home.intl.sg, to: `/dental/${lang}/international/singapore`, id: 'sg' },
        { ...t.home.intl.id, to: `/dental/${lang}/international/indonesia-batam`, id: 'id' },
    ];
    return (
        <section className="bg-ink py-16 text-white md:py-24" data-testid="home-international">
            <Container>
                <SectionHeader light eyebrow={t.home.intl.eyebrow} title={t.home.intl.title} />
                <div className="grid gap-4 md:grid-cols-2">
                    {cards.map((c, i) => (
                        <Reveal key={c.id} delay={i * 0.08} className="h-full">
                            <Link
                                to={c.to}
                                data-testid={`international-cta-${c.id}`}
                                onClick={() => track(c.id === 'sg' ? 'singapore_cta' : 'indonesia_cta', { source: 'home' })}
                                className="group flex h-full flex-col justify-between rounded-3xl border border-white/15 p-7 transition-colors duration-300 hover:bg-white/5 md:p-9"
                            >
                                <div>
                                    <h3 className="font-serif text-2xl font-semibold md:text-3xl">{c.title}</h3>
                                    <p className="mt-3 max-w-md text-[15px] leading-relaxed text-peri-200">{c.text}</p>
                                </div>
                                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-peri-300">
                                    {t.home.intl.cta}
                                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <div className="mt-8 rounded-3xl border border-white/10 p-7 md:p-8">
                        <p className="max-w-3xl text-base leading-relaxed text-peri-200">{t.home.intl.aligner}</p>
                        <p className="mt-3 max-w-3xl text-xs italic leading-relaxed text-peri-500">{t.home.intl.note}</p>
                    </div>
                </Reveal>
            </Container>
        </section>
    );
}

function KolumTeaser() {
    const { t, lang } = useLang();
    const [articles, setArticles] = useState(null);
    useEffect(() => {
        fetchArticles(lang)
            .then((list) => {
                if (list.length > 0) {
                    setArticles(list);
                    return null;
                }
                return fetchArticles().then(setArticles);
            })
            .catch(() => setArticles([]));
    }, [lang]);
    return (
        <section className="py-16 md:py-24" data-testid="home-kolum">
            <Container>
                <SectionHeader eyebrow={t.home.kolum.eyebrow} title={t.home.kolum.title} sub={t.home.kolum.sub} />
                <Reveal>
                    <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.kolum.tagline}</p>
                </Reveal>
                {articles === null ? (
                    <div className="grid gap-5 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-3xl bg-peri-100" />)}</div>
                ) : (
                    <div className="grid gap-5 md:grid-cols-3">
                        {articles.slice(0, 3).map((a, i) => (
                            <Reveal key={a.slug} delay={i * 0.07} className="h-full">
                                <ArticleCard article={a} />
                            </Reveal>
                        ))}
                    </div>
                )}
                {articles && articles.length === 0 && (
                    <div className="rounded-3xl border border-dashed border-peri-300 p-10 text-center text-ink-muted">{t.home.kolum.empty}</div>
                )}
                <Reveal>
                    <div className="mt-10 text-center">
                        <Link to={`/dental/${lang}/kolum`} className="inline-flex items-center gap-2 rounded-full border-2 border-gold-500 px-6 py-3 text-sm font-semibold text-ink transition-all hover:bg-gold-50" data-testid="kolum-teaser-cta">
                            {t.home.kolum.cta}
                        </Link>
                    </div>
                </Reveal>
            </Container>
        </section>
    );
}

function Remember() {
    const { t } = useLang();
    const featured = [TESTIMONIALS[0], TESTIMONIALS[1]];
    return (
        <section className="bg-peri-50 py-16 md:py-24" data-testid="home-remember">
            <Container>
                <SectionHeader eyebrow={t.home.remember.eyebrow} title={t.home.remember.title} sub={t.home.remember.sub} />
                <div className="mb-8 flex flex-wrap gap-2">
                    {t.home.remember.themes.map((th, i) => (
                        <Reveal key={th} delay={i * 0.04}>
                            <span className="inline-block rounded-full border border-peri-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-gold-700">{th}</span>
                        </Reveal>
                    ))}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                    {featured.map((q, i) => (
                        <Reveal key={q.id} delay={i * 0.08}>
                            <div className="h-full rounded-3xl border border-peri-200 bg-white p-8" data-testid={`home-quote-${q.id}`}>
                                <p className="font-serif text-xl italic leading-relaxed text-ink">{q.quote}</p>
                                <div className="mt-5 flex items-center justify-between text-xs text-ink-subtle">
                                    <span className="inline-flex items-center gap-2 font-semibold text-gold-700">
                                        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none"><path d="M3 8.5 6.5 12 13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        {t.home.remember.verifiedLabel}
                                    </span>
                                    <span>{q.lang.toUpperCase()}</span>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Container>
        </section>
    );
}

function Nervous() {
    const { t } = useLang();
    return (
        <section className="py-16 md:py-24" data-testid="home-nervous">
            <Container>
                <Reveal>
                    <div className="rounded-3xl border border-peri-300 bg-gradient-to-r from-peri-100 to-peri-50 p-8 md:p-12">
                        <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
                            <div>
                                <h2 className="font-serif text-3xl font-semibold text-ink md:text-4xl">{t.home.nervous.title}</h2>
                                <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-muted">{t.home.nervous.text}</p>
                            </div>
                            <WhatsAppButton label={t.home.nervous.cta} source="nervous" />
                        </div>
                    </div>
                </Reveal>
            </Container>
        </section>
    );
}

function Location() {
    const { t } = useLang();
    const l = t.home.location;
    return (
        <section className="bg-peri-50 py-16 md:py-24" data-testid="home-location">
            <Container>
                <SectionHeader eyebrow={l.eyebrow} title={l.title} />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[
                        { label: l.addressLabel, value: l.addressValue, slot: true },
                        { label: l.hoursLabel, value: l.hoursValue, slot: true },
                        { label: l.whatsappLabel, value: '011-3306 5735', link: 'https://wa.me/601133065735' },
                        { label: l.emailLabel, value: 'klinikpergigianirmaariandi@gmail.com', link: `mailto:klinikpergigianirmaariandi@gmail.com` },
                    ].map((item, i) => (
                        <Reveal key={item.label} delay={i * 0.06} className="h-full">
                            <div className="h-full rounded-2xl border border-peri-200 bg-white p-6">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{item.label}</p>
                                {item.link ? (
                                    <a href={item.link} className="mt-3 block break-all text-sm font-medium text-ink hover:text-gold-700" data-testid={`location-${item.label.toLowerCase()}`}>{item.value}</a>
                                ) : (
                                    <p className="mt-3 text-sm font-medium text-ink-subtle">{item.value}</p>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <figure className="mt-4">
                        <img src="/img/clinic-building.jpg" alt="Plaza Larkin, Johor Bahru — Klinik Pergigian Irma Ariandi" loading="lazy" className="aspect-[16/7] w-full rounded-3xl border border-peri-200 object-cover" data-testid="location-building-img" />
                        <figcaption className="mt-3 text-center text-xs italic text-ink-subtle">{l.mapNote}</figcaption>
                    </figure>
                </Reveal>
            </Container>
        </section>
    );
}

function FinalCta() {
    const { t, lang } = useLang();
    return (
        <section className="bg-ink py-20 text-white md:py-28" data-testid="home-final-cta">
            <Container>
                <Reveal className="mx-auto max-w-3xl text-center">
                    <p className="font-serif text-4xl font-semibold leading-tight md:text-6xl">{t.home.finalCta.title}</p>
                    <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-peri-200 md:text-lg">{t.home.finalCta.sub}</p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                        <WhatsAppButton label={t.home.finalCta.primary} source="final" />
                        <BookButton label={t.home.finalCta.secondary} lang={lang} source="final" className="!border-peri-300 !text-peri-200 hover:!bg-white/10" />
                    </div>
                </Reveal>
            </Container>
        </section>
    );
}

export default function Home() {
    const { t, lang } = useLang();
    return (
        <>
            <SEO
                title={t.meta.home.title}
                description={t.meta.home.description}
                path="/"
                lang={lang}
            />
            <Hero />
            <Marquee />
            <Pathways />
            <Pillars />
            <Veneers />
            <Why />
            <Digital />
            <Doctors />
            <Teen />
            <International />
            <KolumTeaser />
            <Remember />
            <section className="py-16 md:py-24" data-testid="home-journey">
                <Container>
                    <SectionHeader eyebrow={t.home.journey.eyebrow} title={t.home.journey.title} />
                    <Journey />
                </Container>
            </section>
            <Nervous />
            <Location />
            <FinalCta />
        </>
    );
}
